// bm-design-system: video testimonial
//
// A featured client video testimonial: a self-hosted, lazy-loaded clip
// (<video preload="none"> so the file only downloads on play) with a themed
// play-button overlay, paired with the client's name, an impact statement, and a
// pulled quote. Video sits beside the text on desktop, stacked on mobile.
//
// Native browser controls can't be restyled reliably, so we theme the part that
// matters: a branded teal play button over the poster (matching the carousel
// arrows). Before play — and again after the clip ends — a poster overlay covers
// the video, so it never sits on a raw black end frame. Once playing, the
// browser's native controls take over. SSR-safe: initial state is "not started",
// so the poster + play button render on the server; play is a client-only click.
import * as React from "react";
import { Film, Play } from "lucide-react";
import { cn } from "@/lib/utils";

export interface VideoTestimonialProps {
  /** URL of the self-hosted MP4 (e.g. /media/clip.mp4). Omit when `placeholder`. */
  videoSrc?: string;
  /** Poster image shown before play. Omit when `placeholder`. */
  poster?: string;
  /** Stub card for a testimonial whose clip isn't ready. Renders a "video coming
   *  soon" media panel instead of a player, so it can't 404. Gate the whole card
   *  to development at the call site so it never ships without a real video. */
  placeholder?: boolean;
  /** Client / company name shown as the heading, e.g. "Econo-Pak". */
  company?: React.ReactNode;
  /** Optional client logo shown as a chip beside the company heading. */
  logo?: string;
  /** Descriptor line under the company, e.g. "Target customer: Fortune 500 …". */
  subheader?: React.ReactNode;
  /** Small category tag above the impact facts, e.g. "Impact". */
  impactTag?: React.ReactNode;
  /** Impact proof points, rendered as a scannable bulleted list. */
  impactFacts?: React.ReactNode[];
  quote: React.ReactNode;
  /** Who said the quote. Omit to show the quote without an attribution line
   *  (e.g. repeat quotes from the same person already named on an earlier card). */
  name?: string;
  /** Their role (company is the heading above), e.g. "Sales and Marketing Manager". */
  title?: React.ReactNode;
  /** Optional call-to-action rendered under the attribution (e.g. a Button that
   *  links to a full case study). Passed as a node so the primitive stays
   *  routing-agnostic — the caller decides an Inertia Link vs a plain anchor. */
  cta?: React.ReactNode;
  /** Default playback speed applied once the clip loads. Default 1. */
  playbackRate?: number;
  /** Put the video on the right (text left) on desktop. Default false (video left). */
  reverse?: boolean;
  className?: string;
}

export function VideoTestimonial({
  videoSrc,
  poster,
  placeholder = false,
  company,
  logo,
  subheader,
  impactTag,
  impactFacts,
  quote,
  name,
  title,
  cta,
  playbackRate = 1,
  reverse = false,
  className,
}: VideoTestimonialProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  // "started" = playback has begun and the clip hasn't ended. Drives whether the
  // poster/play overlay is shown and whether native controls are exposed.
  const [started, setStarted] = React.useState(false);

  // Apply the desired speed. Set BOTH defaultPlaybackRate and playbackRate: the
  // browser resets playbackRate to defaultPlaybackRate at points during the load
  // sequence, so setting only playbackRate silently snaps back to 1x on play.
  const applyRate = (v: HTMLVideoElement | null) => {
    if (!v) return;
    v.defaultPlaybackRate = playbackRate;
    v.playbackRate = playbackRate;
  };

  const handlePlayClick = () => {
    applyRate(videoRef.current);
    videoRef.current?.play();
  };

  // Whether anything renders above the quote (company / subheader / impact
  // block). The divider separates that header content from the quote, so it
  // only renders when there is something to separate — otherwise a quote-only
  // card would show an orphaned rule floating in the centered whitespace.
  const hasHeader = Boolean(
    logo || company || subheader || impactTag || (impactFacts && impactFacts.length > 0),
  );

  return (
    <figure
      className={cn(
        "grid gap-8 rounded-3xl border border-hairline bg-page p-6 sm:p-8 md:grid-cols-2 md:items-center",
        className,
      )}
    >
      <div
        className={cn(
          "relative overflow-hidden rounded-2xl border border-hairline bg-surface",
          reverse && "md:order-2",
        )}
      >
        {placeholder ? (
          // No clip yet: a neutral "coming soon" panel in place of the player, at
          // the same aspect ratio as a real cropped clip (912x714).
          <div className="flex aspect-[912/714] flex-col items-center justify-center gap-3 text-ink-muted">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-page">
              <Film className="h-7 w-7" aria-hidden="true" />
            </span>
            <span className="text-sm">Video coming soon</span>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              className="block w-full"
              controls={started}
              playsInline
              preload="none"
              poster={poster}
              onLoadedMetadata={(e) => applyRate(e.currentTarget)}
              onCanPlay={(e) => applyRate(e.currentTarget)}
              onPlay={(e) => {
                applyRate(e.currentTarget);
                setStarted(true);
              }}
              onPlaying={(e) => applyRate(e.currentTarget)}
              onEnded={() => setStarted(false)}
            >
              <source src={videoSrc} type="video/mp4" />
            </video>

            {!started && (
              <button
                type="button"
                onClick={handlePlayClick}
                aria-label={name ? `Play video testimonial from ${name}` : "Play video testimonial"}
                className="group/play absolute inset-0 flex items-center justify-center"
              >
                {/* Poster overlay hides the raw first/last video frame. */}
                <img
                  src={poster}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <span
                  aria-hidden="true"
                  className="absolute inset-0 bg-ink-display/20 transition-colors group-hover/play:bg-ink-display/30"
                />
                <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-accent text-page shadow-lg transition-transform group-hover/play:scale-105">
                  <Play
                    className="h-7 w-7 translate-x-0.5"
                    fill="currentColor"
                    aria-hidden="true"
                  />
                </span>
              </button>
            )}
          </>
        )}
      </div>

      <div className={cn("text-center md:text-left", reverse && "md:order-1")}>
        {(logo || company) && (
          <div className="flex items-center justify-center gap-3 md:justify-start">
            {logo && (
              // Client logo chip. White tile + object-contain so marks with odd
              // aspect ratios or their own background sit cleanly. Decorative —
              // the company name sits beside it.
              <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-hairline bg-white">
                <img src={logo} alt="" className="h-full w-full object-contain p-1" />
              </span>
            )}
            {company && (
              <p className="font-display text-2xl font-semibold text-ink-display">
                {company}
              </p>
            )}
          </div>
        )}
        {subheader && (
          <p className="mt-1 text-sm text-ink-muted">{subheader}</p>
        )}

        {(impactTag || (impactFacts && impactFacts.length > 0)) && (
          <div className="mt-4">
            {impactTag && (
              <div>
                <span className="badge badge-accent">{impactTag}</span>
              </div>
            )}
            {impactFacts && impactFacts.length > 0 && (
              <ul className="mt-4 inline-flex flex-col gap-2.5 text-left">
                {impactFacts.map((fact, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-ink-display">
                    <span
                      aria-hidden="true"
                      className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
                    />
                    <span>{fact}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {hasHeader && <div className="my-6 border-t border-hairline" />}

        <blockquote className="testimonial-quote md:text-left">
          {quote}
        </blockquote>
        {(name || title) && (
          <figcaption className="mt-4">
            {name && <strong className="block">{name}</strong>}
            {title && (
              <span className="mt-0.5 block text-sm text-ink-muted">{title}</span>
            )}
          </figcaption>
        )}

        {cta && <div className="mt-6">{cta}</div>}
      </div>
    </figure>
  );
}
